import { isValidTckn } from 'src/app/core/helpers/identity-document.helpers';
import { FrontSideResult, IdentityOcrResult, MrzTd1Result, MrzTd3Result, OcrFieldStatus } from './identity-ocr.models';
import { matchMrzNames } from './mrz-parser';

/**
 * Ön ve arka yüz okumalarını birleştirir (masak-kimlik-ocr.md §4 adım 5):
 * - TCKN, seri no, doğum tarihi, son geçerlilik: kontrol hanesi tutan MRZ değeri "doğrulandı";
 *   MRZ yoksa/tutmuyorsa ön yüz değeri "kontrol edin". İki kaynak çelişirse "kontrol edin" + uyarı.
 * - Ad/soyad: yalnızca ön yüz sözcükleri MRZ ad satırına oturursa — Türkçe karakterli, kimlikteki
 *   gibi büyük harf (K4). Oturmazsa önerilmez: yanlış ad kayıtlı müşteride kaydı engeller (K7).
 */
export function mergeIdentityOcr(back: MrzTd1Result | null, front: FrontSideResult | null): IdentityOcrResult {
  const warnings: string[] = [];

  const pick = (
    label: string,
    mrzValue: string | undefined,
    mrzOk: boolean,
    frontValue: string | undefined,
  ): { value?: string; status: OcrFieldStatus } => {
    if (mrzValue && mrzOk) {
      if (frontValue && frontValue !== mrzValue) {
        warnings.push(`${label} ön ve arka yüzde farklı okundu — kontrol ediniz.`);
        return { value: mrzValue, status: 'unverified' };
      }
      return { value: mrzValue, status: 'verified' };
    }
    if (frontValue) {
      return { value: frontValue, status: mrzValue && mrzValue === frontValue ? 'verified' : 'unverified' };
    }
    return mrzValue ? { value: mrzValue, status: 'unverified' } : { status: 'missing' };
  };

  // TCKN kendi algoritmasıyla denetleniyor; bileşik hane de tutarsa ya da ön yüzle aynıysa doğrulandı.
  const tckn = pick('T.C. kimlik numarası', back?.tckn, !!back?.tcknOk && !!back?.compositeOk, front?.tckn);
  // Algoritmaya uymayan TCKN hiç önerilmez.
  if (tckn.value && !isValidTckn(tckn.value)) {
    tckn.value = undefined;
    tckn.status = 'missing';
  }
  const serial = pick('Seri no', back?.serialNumber, !!back?.serialNumberOk, front?.serialNumber);
  const birth = pick('Doğum tarihi', back?.birthDate, !!back?.birthDateOk, front?.birthDate);
  const expiry = pick('Son geçerlilik tarihi', back?.expiryDate, !!back?.expiryDateOk, front?.expiryDate);

  let firstName: string | undefined;
  let lastName: string | undefined;
  let nameStatus: OcrFieldStatus = 'missing';
  if (back && front) {
    const names = matchMrzNames(front.upperWords, back.nameLine);
    if (names) {
      firstName = names.given;
      lastName = names.surname;
      nameStatus = names.fromMrzOnly ? 'unverified' : 'verified';
      if (names.fromMrzOnly) {
        warnings.push('Adın bir kısmı yalnızca arka yüzden okundu (Türkçe karaktersiz) — ad ve soyadı kontrol ediniz.');
      }
    } else {
      warnings.push('Ad ve soyad okunamadı — kartı kırpıp yeniden okuyun ya da elle giriniz.');
    }
  } else if (back) {
    warnings.push('Adı Türkçe karakterleriyle okumak için kartın ön yüzünü de yükleyiniz.');
  } else if (front) {
    warnings.push('Kartın arka yüzü (MRZ) yüklenmedi — okunan bilgiler doğrulanamadı.');
  }

  // Bileşik hane satır sonunda, en kötü okunan yerde — tek başına tutmaması uyarı değil.
  const fieldChecks = back ? [back.serialNumberOk, back.birthDateOk, back.expiryDateOk] : [];
  if (fieldChecks.some((ok) => ok) && fieldChecks.some((ok) => !ok)) {
    warnings.push('Arka yüzdeki kontrol hanelerinin bir kısmı tutmadı — işaretli alanları kontrol ediniz.');
  }

  return {
    tckn: tckn.value,
    serialNumber: serial.value,
    firstName,
    lastName,
    birthDate: birth.value,
    expiryDate: expiry.value,
    nationalityCode: back?.nationalityCode && /^[A-Z]{3}$/.test(back.nationalityCode) ? back.nationalityCode : undefined,
    status: {
      tckn: tckn.status,
      serialNumber: serial.status,
      name: nameStatus,
      birthDate: birth.status,
      expiryDate: expiry.status,
    },
    documentKind: 'tckn',
    mrzFound: !!back,
    warnings,
  };
}

/**
 * Pasaport: fotoğraflı sayfanın MRZ'si (TD3) asıl kaynak; aynı sayfanın Türkçe okuması yalnızca adı
 * Türkçe karakterleriyle vermek ve pasaport no / TCKN'yi çapraz denetlemek için. Tarihler sayfada
 * "30 OCA/JAN 1982" biçiminde — MRZ'den alınır. Kişisel numara (Türk pasaportunda TCKN) yoksa TCKN
 * "okunamadı" değil, boş kalır: yabancı pasaportta TCKN beklenmez.
 */
export function mergePassportOcr(mrz: MrzTd3Result | null, page: FrontSideResult | null): IdentityOcrResult {
  const warnings: string[] = [];
  const status = (ok: boolean, value?: string): OcrFieldStatus => (!value ? 'missing' : ok ? 'verified' : 'unverified');

  if (!mrz) {
    warnings.push('Pasaportun MRZ satırları okunamadı — fotoğraflı sayfayı düz ve tam görünecek biçimde çekin.');
    return {
      tckn: page?.tckn,
      status: { tckn: page?.tckn ? 'unverified' : 'missing', serialNumber: 'missing', name: 'missing', birthDate: 'missing', expiryDate: 'missing' },
      documentKind: 'passport',
      mrzFound: false,
      warnings,
    };
  }

  // Sayfadaki TCKN MRZ'dekiyle aynıysa ya da bileşik hane tutuyorsa doğrulandı.
  const tcknOk = !!mrz.tckn && (mrz.compositeOk || page?.tckn === mrz.tckn);
  if (mrz.tckn && page?.tckn && page.tckn !== mrz.tckn) {
    warnings.push('T.C. kimlik numarası MRZ ile sayfada farklı okundu — kontrol ediniz.');
  }

  let firstName: string | undefined;
  let lastName: string | undefined;
  let nameStatus: OcrFieldStatus = 'missing';
  const names = page ? matchMrzNames(page.upperWords, mrz.nameLine) : null;
  if (names) {
    firstName = names.given;
    lastName = names.surname;
    nameStatus = names.fromMrzOnly ? 'unverified' : 'verified';
    if (names.fromMrzOnly) {
      warnings.push('Adın bir kısmı yalnızca MRZ\'den okundu (Türkçe karaktersiz) — ad ve soyadı kontrol ediniz.');
    }
  } else {
    warnings.push('Ad ve soyad okunamadı — sayfayı kırpıp yeniden okuyun ya da elle giriniz.');
  }

  const checks = [mrz.passportNumberOk, mrz.birthDateOk, mrz.expiryDateOk];
  if (checks.some((ok) => ok) && checks.some((ok) => !ok)) {
    warnings.push('MRZ kontrol hanelerinin bir kısmı tutmadı — işaretli alanları kontrol ediniz.');
  }

  return {
    tckn: mrz.tckn,
    serialNumber: mrz.passportNumber || undefined,
    firstName,
    lastName,
    birthDate: mrz.birthDate,
    expiryDate: mrz.expiryDate,
    nationalityCode: /^[A-Z]{3}$/.test(mrz.nationalityCode) ? mrz.nationalityCode : undefined,
    status: {
      tckn: status(tcknOk, mrz.tckn),
      serialNumber: status(mrz.passportNumberOk, mrz.passportNumber),
      name: nameStatus,
      birthDate: status(mrz.birthDateOk, mrz.birthDate),
      expiryDate: status(mrz.expiryDateOk, mrz.expiryDate),
    },
    documentKind: 'passport',
    mrzFound: true,
    warnings,
  };
}
